import { useCallback } from "react";

import { assertNever } from "@impero-com/common/utils/obj";
import moment from "moment-timezone";

import { ActivityTrendBucket } from "api/generated/model";
import {
    HEALTH_FILTER_KEYS,
    HealthFilterKey,
} from "org/controlActivities/list/healthFilter";
import { useActivitiesUrlDependencies } from "org/dashboard/hooks/useActivitiesUrlDependencies";
import { buildCanonicalActivitiesUrl } from "org/dashboard/utils/activitiesUrl";

import { ActivityTrendSeverity } from "./activityTrendStorage";
import { useControlHealthFilters } from "./filters/ControlHealthFiltersProvider";

export function useBuildTrendBucketUrl(
    severity: ActivityTrendSeverity
): (bucket: ActivityTrendBucket) => string {
    const { filters } = useControlHealthFilters();
    const dependencies = useActivitiesUrlDependencies();

    return useCallback(
        (bucket) =>
            buildCanonicalActivitiesUrl(
                {
                    dateRange: {
                        start: moment(bucket.start).startOf("day"),
                        end: moment(bucket.end).endOf("day"),
                    },
                    statusKey: null,
                    filteredControlProgramIds: filters.controlPrograms,
                    filteredTags: filters.tags,
                    filteredEntityIds: filters.entities,
                    showArchivedActivities: filters.showArchivedActivities,
                    showInactivePrograms: filters.showInactivePrograms,
                    healthFilter: severityToHealthFilter(severity),
                },
                dependencies
            ),
        [filters, dependencies, severity]
    );
}

function severityToHealthFilter(
    severity: ActivityTrendSeverity
): HealthFilterKey | undefined {
    switch (severity) {
        case "all":
            return undefined;
        case "highFriction":
            return HEALTH_FILTER_KEYS.HIGH_FRICTION;
        case "friction":
            return HEALTH_FILTER_KEYS.FRICTION;
        case "noFriction":
            return HEALTH_FILTER_KEYS.CLEAN;
        default:
            return assertNever(severity);
    }
}
